/**
 * A Code 128 encoder (set B, printable ASCII), for serial numbers as linear
 * barcodes that any handheld scanner reads.
 *
 * Set B only: a serial is mixed letters and digits, so set C's digit pairs
 * would rarely apply, and one set keeps this a table and a checksum. The table
 * is the standard's; `code128.test.ts` checks its shape, and the encoder was
 * checked against a real decoder (zxing) before it was pinned.
 */

/** Bar, space, bar, space, bar, space widths for symbols 0 to 105, then the stop (13 modules). */
const PATTERNS = [
  "212222",
  "222122",
  "222221",
  "121223",
  "121322",
  "131222",
  "122213",
  "122312",
  "132212",
  "221213",
  "221312",
  "231212",
  "112232",
  "122132",
  "122231",
  "113222",
  "123122",
  "123221",
  "223211",
  "221132",
  "221231",
  "213212",
  "223112",
  "312131",
  "311222",
  "321122",
  "321221",
  "312212",
  "322112",
  "322211",
  "212123",
  "212321",
  "232121",
  "111323",
  "131123",
  "131321",
  "112313",
  "132113",
  "132311",
  "211313",
  "231113",
  "231311",
  "112133",
  "112331",
  "132131",
  "113123",
  "113321",
  "133121",
  "313121",
  "211331",
  "231131",
  "213113",
  "213311",
  "213131",
  "311123",
  "311321",
  "331121",
  "312113",
  "312311",
  "332111",
  "314111",
  "221411",
  "431111",
  "111224",
  "111422",
  "121124",
  "121421",
  "141122",
  "141221",
  "112214",
  "112412",
  "122114",
  "122411",
  "142112",
  "142211",
  "241211",
  "221114",
  "413111",
  "241112",
  "134111",
  "111242",
  "121142",
  "121241",
  "114212",
  "124112",
  "124211",
  "411212",
  "421112",
  "421211",
  "212141",
  "214121",
  "412121",
  "111143",
  "111341",
  "131141",
  "114113",
  "114311",
  "411113",
  "411311",
  "113141",
  "114131",
  "311141",
  "411131",
  "211412",
  "211214",
  "211232",
] as const;
const STOP = "2331112";
const START_B = 104;

export const CODE128_MAX_CHARS = 40;

export type Code128Result = { ok: true; modules: boolean[] } | { ok: false; reason: string };

/** Modules left to right, true for a bar. The quiet zone is the caller's. */
export function encodeCode128(text: string): Code128Result {
  if (text.length === 0) return { ok: false, reason: "There is nothing to put in the barcode." };
  if (text.length > CODE128_MAX_CHARS) {
    return {
      ok: false,
      reason: `That is ${text.length} characters; a barcode label holds at most ${CODE128_MAX_CHARS}.`,
    };
  }

  const values: number[] = [];
  for (const char of text) {
    const code = char.codePointAt(0) ?? 0;
    if (code < 32 || code > 126) {
      return {
        ok: false,
        reason: `"${char}" cannot go in this barcode; it takes letters, digits and common punctuation.`,
      };
    }
    values.push(code - 32);
  }

  const checksum = values.reduce((sum, value, index) => sum + value * (index + 1), START_B) % 103;
  const symbols = [PATTERNS[START_B], ...values.map((value) => PATTERNS[value]), PATTERNS[checksum], STOP];

  const modules: boolean[] = [];
  for (const pattern of symbols) {
    let bar = true;
    for (const width of pattern ?? "") {
      for (let i = 0; i < Number(width); i++) modules.push(bar);
      bar = !bar;
    }
  }
  return { ok: true, modules };
}
